import { createStyles, makeStyles, Theme } from '@material-ui/core';
import MuiCreateIcon from '@material-ui/icons/Create';
import MuiDeleteIcon from '@material-ui/icons/Delete';
import DescriptionIcon from '@material-ui/icons/Description';
import MuiAnalyticsIcon from '@material-ui/icons/InsertChart';
import MuiSyncIcon from '@material-ui/icons/Sync';
import VisibilityIconMUI from '@material-ui/icons/Visibility';
import React from 'react';

import { ReactComponent as PublishSvg } from '../../../../assets/publish.svg';
import { ReactComponent as ShareSvg } from '../../../../assets/share.svg';
import { ReactComponent as UnpublishSvg } from '../../../../assets/unpublish.svg';
import {
  PostDetailsFragment,
  PostType,
  useDeletePostMutation,
  usePublishPostMutation,
  useWatchPostMutation,
} from '../../../../graphql/gen/types';
import { useConfirmAction } from '../../../../providers/plex_provider';
import { spacings } from '../../../../theme';
import { ButtonColor } from '../../../shared/primitives/PlexButton';
import { PlexIconButton } from '../../../shared/primitives/PlexIconButton';
import { PlexIconButtonHref } from '../../../shared/primitives/PlexIconButtonHref';
import { useDialogState } from '../../dialog/useDialogState';
import { SharePopover } from '../../feed/SharePopover';
import { useSharePopover } from '../../feed/useSharePopover';
import { canDeletePost, canEditPost, canPublishPost, canUnpublishPost, canViewPostAnalytics } from '../../permissions';
import { logAnalytics } from '../../shared/logAnalytics';

type Props = {
  plexPostData: PostDetailsFragment;
};

export function PlexPostActions(props: Props): React.ReactElement {
  const [, setDialogState] = useDialogState();
  const [shareAnchorEl, handleClickShare, handleCloseShare] = useSharePopover();
  const { plexPostData } = props;
  const { id } = plexPostData;
  const [deletePost, { loading: deleteLoading }] = useDeletePostMutation();
  const [publishPost, { loading: publishingLoading }] = usePublishPostMutation();
  const [watchPost, { loading: watchLoading }] = useWatchPostMutation();
  const confirmAction = useConfirmAction();

  const classes = useStyles();

  const handleDelete = (): void => {
    confirmAction({
      title: 'Confirmation',
      message: 'Are you sure you want to permanently delete this post?',
      confirmButton: {
        onClick: (): void => {
          deletePost({ variables: { postId: id } });
          logAnalytics({ name: 'post deleted', details: { plexId: plexPostData.plex.id, itemId: id } });
        },
        label: 'Delete',
        color: ButtonColor.RedBackground,
      },
    });
  };

  const handleSetEditDialog = (): void => {
    if (plexPostData.type === PostType.Event) {
      setDialogState({ type: 'edit_event', postId: id });
    } else {
      setDialogState({ type: 'edit_post', postId: id });
    }
  };

  const handlePublish = (): void => {
    if (plexPostData != null) {
      publishPost({
        variables: {
          publishPostInput: {
            postId: plexPostData.id,
            checked: true,
          },
        },
      });
      logAnalytics({ name: 'post published', details: { plexId: plexPostData.plex.id, itemId: id } });
    }
  };

  const handleUnpublish = (): void => {
    if (plexPostData != null) {
      publishPost({
        variables: {
          publishPostInput: {
            postId: plexPostData.id,
            checked: false,
          },
        },
      });
      logAnalytics({ name: 'post unpublished', details: { plexId: plexPostData.plex.id, itemId: id } });
    }
  };

  const handleWatch = (): void => {
    if (plexPostData != null) {
      watchPost({
        variables: {
          payload: {
            postId: plexPostData.id,
            watching: !plexPostData.watching,
          },
        },
      });
      logAnalytics({
        name: plexPostData.watching ? 'post watching removed' : 'post watching added',
        details: { plexId: plexPostData.plex.id, itemId: id },
      });
    }
  };

  const handleOpenAnalytics = (): void => {
    setDialogState({ type: 'post_analytics_from_post', postId: id });
  };

  const handleSync = (): void => {
    setDialogState({ type: 'sync_gdocs_post', postId: id });
  };

  const { publishStatus: status, currentUserRelation: postRelation, plex, source } = plexPostData;
  const plexRelation = plex.currentUserRelation.state;

  const disabled = deleteLoading || publishingLoading || watchLoading;

  return (
    <div className={classes.root}>
      {canEditPost({ plexRelation, postRelation }) && source != null && (
        <PlexIconButtonHref
          className={classes.actionButton}
          MuiIcon={DescriptionIcon}
          href={source.url}
          color={ButtonColor.Purple}
          tooltip={'View Source - Google Document'}
        />
      )}

      {canEditPost({ plexRelation, postRelation }) && source != null && (
        <PlexIconButton
          className={classes.actionButton}
          MuiIcon={MuiSyncIcon}
          onClick={handleSync}
          color={ButtonColor.Purple}
          tooltip={'Sync from Source - Google Document'}
        />
      )}

      {canEditPost({ plexRelation, postRelation }) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handleSetEditDialog}
          MuiIcon={MuiCreateIcon}
          color={ButtonColor.Purple}
          tooltip={'Edit'}
        />
      )}
      <PlexIconButton
        className={classes.actionButton}
        disabled={disabled}
        onClick={handleClickShare}
        SvgIcon={ShareSvg}
        color={ButtonColor.Purple}
        tooltip={'Share'}
      />
      <SharePopover itemId={id} itemType="post" anchorEl={shareAnchorEl} handleClose={handleCloseShare} />
      <PlexIconButton
        className={classes.actionButton}
        disabled={disabled}
        onClick={handleWatch}
        MuiIcon={VisibilityIconMUI}
        color={plexPostData.watching ? ButtonColor.LightGreen : ButtonColor.Purple}
        tooltip={plexPostData.watching ? 'Stop watching' : 'Watch'}
      />

      {canPublishPost({ plexRelation, status }) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handlePublish}
          SvgIcon={PublishSvg}
          color={ButtonColor.Purple}
          tooltip={'Publish'}
        />
      )}
      {canUnpublishPost({ plexRelation, status }) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handleUnpublish}
          SvgIcon={UnpublishSvg}
          color={ButtonColor.Purple}
          tooltip={'Un-publish'}
        />
      )}
      {canViewPostAnalytics(plexRelation) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handleOpenAnalytics}
          MuiIcon={MuiAnalyticsIcon}
          color={ButtonColor.Purple}
          tooltip={'Analytics'}
        />
      )}
      {canDeletePost({ plexRelation, postRelation }) && (
        <PlexIconButton
          className={classes.actionButton}
          disabled={disabled}
          onClick={handleDelete}
          MuiIcon={MuiDeleteIcon}
          color={ButtonColor.TransparentRed}
          tooltip={'Delete'}
        />
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      position: 'absolute',
      flexDirection: 'column',
      top: 0,
      right: 0,
    },
    actionButton: {
      marginTop: spacings.normal,
      marginRight: spacings.xlarge,
    },
  })
);
